import type { PaketDuzenlemesi } from './paket-duzenleme';
/**
 * Hızlı tatil teklifi: ekranın saf mantığı (ağ yok, React yok). Şartname 5 ile 10 arasındaki bütün kurallar burada
 * tek yerde durur ve birim testle (`api/test/unit/hizli-teklif-ui-mantik.spec.ts`) sınanır; bileşenler yalnız çizer.
 *
 * Kural özeti:
 *  - Çıktı kapısı tek işlev: `ciktiKapisi`. PDF, mesaj, kopyala, indir hepsi ona bakar.
 *  - Fiyatı bilinmeyen paket "dahil" sayılmaz, toplamı açmaz.
 *  - Talebe bağlı fiyat seçilemez.
 *  - Tarih, oda ya da uyruk değişince onay geçersizdir (`talepImzasi` farkı), sessizce korunmaz.
 *  - Çekim sürerken sıra sabit; hepsi gelince bir kez fiyata göre.
 *  - Duyurular en çok 5 sn'de bir (`DuyuruKuyrugu`).
 */
import type {
  DurumKodu,
  DurumSekli,
  DurumTonu,
  IptalKosulu,
  OdaDuzeni,
  OdaGrubu,
  OdaSecenegi,
  OdaTaslagi,
  OtelGirdisi,
  OtelSonucu,
  PaketFiyati,
  PaketKodu,
  AracSecimi,
  KalemBirimi,
  ManuelOtel,
  ParaBirimi,
  SerbestKalem,
  YatBoyutu,
  YatSecimi,
  SecimGrubu,
  SecimOdaSatiri,
  SecimOtel,
  TeklifSecimi,
} from './tipler';

// ---- Politika sayıları (şartname 7, tek yerde) -----------------------------------------------------------

export const POLITIKA = {
  /** Sunucu tarafında uygulanır; ekran yalnız metinde söyler. */
  otelZamanAsimiSn: 25,
  bilgiEsigiMs: 30 * 60_000,
  kritikEsikMs: 2 * 60_000,
  duyuruAraligiMs: 5_000,
  /** Son etkileşimden bu yana bu kadar geçmediyse satırlar yerinde kalır (şartname 10). */
  etkilesimPenceresiMs: 3_000,
  azamiOtel: 12,
  azamiOda: 8,
  /** Sunucu eşzamanlılığı (`istekEszamanliligi`): bekleyenlerin ilk dördü "çekiliyor" sayılır. */
  eszamanliCekim: 4,
  yoklamaMs: 1_000,
} as const;

// ---- Para ve zaman ---------------------------------------------------------------------------------------

const TUTAR = new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** 473787 -> "4.737,87" (tr-TR, tam kuruş). */
export function tutarYaz(minor: number): string {
  return TUTAR.format(Math.round(minor) / 100);
}

export const paraYaz = (minor: number, para: string): string => `${tutarYaz(minor)} ${para}`;

export function kalanMs(bitis: string | null, simdi: number): number | null {
  if (bitis === null) return null;
  const t = Date.parse(bitis);
  return Number.isFinite(t) ? t - simdi : null;
}

export type SureSeviyesi = 'bilinmiyor' | 'ok' | 'bilgi' | 'kritik' | 'doldu';

export function sureSeviyesi(kalan: number | null): SureSeviyesi {
  if (kalan === null) return 'bilinmiyor';
  if (kalan <= 0) return 'doldu';
  if (kalan <= POLITIKA.kritikEsikMs) return 'kritik';
  if (kalan <= POLITIKA.bilgiEsigiMs) return 'bilgi';
  return 'ok';
}

/** 8_040_000 -> "2 sa 14 dk"; saat yoksa "27 dk"; bir dakikanın altı "1 dk"dan az gösterilmez. */
export function kalanMetni(kalan: number): string {
  const dk = Math.max(1, Math.ceil(kalan / 60_000));
  const sa = Math.floor(dk / 60);
  const d = dk % 60;
  return sa > 0 ? `${sa} sa ${String(d).padStart(2, '0')} dk` : `${d} dk`;
}

const AYLAR_KISA = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'] as const;
export function gunAyKisa(iso: string): string {
  const e = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return e === null ? iso : `${Number(e[3])} ${AYLAR_KISA[Number(e[2]) - 1] ?? ''}`;
}

/** Dubai saatiyle "14:32" (saatler Dubai saatiyle). */
export function saatYaz(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat('tr-TR', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone: 'Asia/Dubai' }).format(d);
}

export function sureMetni(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

export function gunSayisi(giris: string, cikis: string): number | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(giris) || !/^\d{4}-\d{2}-\d{2}$/.test(cikis)) return null;
  const f = (Date.parse(`${cikis}T00:00:00Z`) - Date.parse(`${giris}T00:00:00Z`)) / 86_400_000;
  return Number.isInteger(f) && f > 0 ? f : null;
}

// ---- Talep: odalar, yaşlar, hızlı yazım, imza --------------------------------------------------------------

let sayac = 0;
export const yeniOdaKimligi = (): string => `oda-${++sayac}-${Math.random().toString(36).slice(2, 8)}`;

export const yeniOda = (yetiskin = 2): OdaTaslagi => ({ id: yeniOdaKimligi(), yetiskin, cocukYaslari: [] });

export function yasGecerli(y: string): boolean {
  return /^\d{1,2}$/.test(y) && Number(y) >= 0 && Number(y) <= 17;
}

/** Eksik ya da geçersiz yaşların konumu. Boş liste = talep yaş bakımından tamam. */
export function eksikYaslar(odalar: readonly OdaTaslagi[]): readonly { readonly oda: number; readonly cocuk: number }[] {
  const e: { oda: number; cocuk: number }[] = [];
  odalar.forEach((o, i) => o.cocukYaslari.forEach((y, k) => { if (!yasGecerli(y)) e.push({ oda: i, cocuk: k }); }));
  return e;
}

/** 0 yaşındaki çocuk için girilen ay (0-11). Başka yaşta ya da boşken yok sayılır. */
function ayDegeri(yas: string, ay: string | undefined): number | null {
  if (yas.trim() !== '0' || ay === undefined || ay.trim() === '') return null;
  const n = Number(ay);
  return Number.isInteger(n) && n >= 1 && n <= 11 ? n : null;
}

export function odaDuzenleri(odalar: readonly OdaTaslagi[]): OdaDuzeni[] {
  return odalar.map((o) => {
    const aylar = o.cocukYaslari.map((y, k) => ayDegeri(y, o.cocukAylari?.[k]) ?? 0);
    return { yetiskin: o.yetiskin, cocukYaslari: o.cocukYaslari.map(Number), ...(aylar.some((a) => a > 0) ? { cocukAylari: aylar } : {}) };
  });
}

export function odaToplamlari(odalar: readonly { readonly yetiskin: number; readonly cocukYaslari: readonly unknown[] }[]): { readonly oda: number; readonly yetiskin: number; readonly cocuk: number } {
  return { oda: odalar.length, yetiskin: odalar.reduce((t, o) => t + o.yetiskin, 0), cocuk: odalar.reduce((t, o) => t + o.cocukYaslari.length, 0) };
}

/** "2 yetişkin + 2 çocuk" gibi kısa konuk metni. */
export function konukMetni(d: OdaDuzeni): string {
  const y = `${d.yetiskin} yetişkin`;
  return d.cocukYaslari.length === 0 ? y : `${y} + ${d.cocukYaslari.length} çocuk`;
}

export type HizliYazim = { readonly tamam: true; readonly odalar: readonly { readonly yetiskin: number; readonly cocuk: number }[] } | { readonly tamam: false };

/**
 * "3 oda: 2+2çocuk, 2, 2" -> üç oda. Yalnız SAYILARI doldurur: çocuk yaşı tahmin edilmez (şartname 5).
 * Öntakı ("3 oda:") isteğe bağlı; varsa oda sayısıyla uyuşmalı. Çocuk kelimesi: cocuk, çocuk, cocuk, çocuk, cck.
 */
export function hizliYazimiCoz(metin: string): HizliYazim {
  const kucuk = metin.trim().toLocaleLowerCase('tr-TR');
  if (kucuk === '') return { tamam: false };
  let govde = kucuk;
  let beklenen: number | null = null;
  const on = /^(\d{1,2})\s*oda\s*:?\s*(.*)$/u.exec(kucuk);
  if (on !== null) { beklenen = Number(on[1]); govde = on[2] ?? ''; }
  const parcalar = govde.split(/[,;]/u).map((p) => p.trim()).filter((p) => p !== '');
  if (parcalar.length === 0) return { tamam: false };
  const odalar: { yetiskin: number; cocuk: number }[] = [];
  for (const p of parcalar) {
    const e = /^(\d{1,2})(?:\s*\+\s*(\d{1,2})\s*(?:çocuk|cocuk|cck|ç)?)?$/u.exec(p);
    if (e === null) return { tamam: false };
    const yetiskin = Number(e[1]);
    const cocuk = e[2] === undefined ? 0 : Number(e[2]);
    if (yetiskin < 1 || yetiskin > 10 || cocuk > 10) return { tamam: false };
    odalar.push({ yetiskin, cocuk });
  }
  if (odalar.length > POLITIKA.azamiOda) return { tamam: false };
  if (beklenen !== null && beklenen !== odalar.length) return { tamam: false };
  return { tamam: true, odalar };
}

/** Hızlı yazımı mevcut odalara uygular: var olan yaşlar korunur, fazlalık kırpılır, eksik boş eklenir. */
export function hizliYazimiUygula(mevcut: readonly OdaTaslagi[], yazim: { readonly odalar: readonly { readonly yetiskin: number; readonly cocuk: number }[] }): OdaTaslagi[] {
  return yazim.odalar.map((o, i) => {
    const eski = mevcut[i];
    const yaslar = Array.from({ length: o.cocuk }, (_, k) => eski?.cocukYaslari[k] ?? '');
    return { id: eski?.id ?? yeniOdaKimligi(), yetiskin: o.yetiskin, cocukYaslari: yaslar, ...(eski?.cocukAylari === undefined ? {} : { cocukAylari: eski.cocukAylari.slice(0, o.cocuk) }) };
  });
}

export interface TalepGirdisi {
  readonly giris: string;
  readonly cikis: string;
  readonly odalar: readonly OdaTaslagi[];
  readonly uyruk: string;
}

/**
 * Talep imzası: sonucun hangi talebe ait olduğunu söyler. Tarih, oda dağılımı, yaşlar ve uyruk girer; otel listesi
 * girmez (otel tercihi revizyonda korunur, şartname 9). Oda kimliği girmez: yalnız içerik.
 */
/** Ay yalnız varsa imzaya girer (ay yoksa imza eskisiyle aynı kalır); ay değişince sonuç "eski talep" olur ve fiyat yeniden çekilir. */
function ayImzasi(yaslar: readonly string[], aylar: readonly string[] | undefined): readonly (readonly (number | null)[])[] {
  const a = yaslar.map((y, k) => ayDegeri(y, aylar?.[k]));
  return a.some((x) => x !== null) ? [a] : [];
}

export function talepImzasi(t: TalepGirdisi): string {
  return JSON.stringify([t.giris, t.cikis, t.uyruk, t.odalar.map((o) => [o.yetiskin, o.cocukYaslari.map((y) => y.trim()), ...ayImzasi(o.cocukYaslari, o.cocukAylari)])]);
}

export function imzaIcinDuzen(d: readonly OdaDuzeni[], giris: string, cikis: string, uyruk: string): string {
  return JSON.stringify([giris, cikis, uyruk, d.map((o) => [o.yetiskin, o.cocukYaslari.map(String), ...(o.cocukAylari?.some((a) => a > 0) === true ? [o.cocukAylari] : [])])]);
}

// ---- Otel girdisi ve sonuç eşleme ---------------------------------------------------------------------------

export function otelGirdisiOlustur(o: { otelNo: string | null; slug: string; ad: string; yildiz: number | null; sehir?: string | null; ulke?: string | null }): OtelGirdisi {
  return { girdi: o.otelNo ?? o.slug, ad: o.ad, otelNo: o.otelNo, slug: o.slug, yildiz: o.yildiz, sehir: o.sehir ?? null, ulke: o.ulke ?? null };
}

// ---- Destinasyon, başlık ve paket görünürlüğü (Musa kararı 26.10.2026: ekran Dubai'ye bağlı değil) ------------

/** Türkçe harf ve aksan katlama: "Abu Dabi", "ABU DHABI", "Dubai" karşılaştırması. */
const katla = (m: string): string => m.toLocaleLowerCase('tr-TR').replace(/ı/g, 'i').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, ' ').trim();

/** Otelin destinasyonu: şehir, yoksa ülke. */
export const otelDestinasyonu = (o: Pick<OtelGirdisi, 'sehir' | 'ulke'>): string => (o.sehir ?? o.ulke ?? '').trim();

/**
 * Seçili otellerden destinasyon: tek şehir ise şehir; birden çok şehir ama tek ülke ise ülke; birden çok ülke ise
 * "Çoklu Rota". Otel yoksa boş. Elle düzenlenebilir (ekranda `destinasyonElle` bunu ezer).
 */
export function destinasyonOner(oteller: readonly Pick<OtelGirdisi, 'sehir' | 'ulke'>[]): string {
  if (oteller.length === 0) return '';
  const sehirler = new Map<string, string>();
  for (const o of oteller) { const k = katla(otelDestinasyonu(o)); if (k !== '' && !sehirler.has(k)) sehirler.set(k, otelDestinasyonu(o)); }
  if (sehirler.size === 0) return '';
  if (sehirler.size === 1) return [...sehirler.values()][0] ?? '';
  const ulkeler = new Set(oteller.map((o) => katla(o.ulke ?? '')).filter((k) => k !== ''));
  if (ulkeler.size === 1) return oteller.find((o) => (o.ulke ?? '').trim() !== '')?.ulke?.trim() ?? '';
  return 'Çoklu Rota';
}

/** "<Müşteri adı> - Özel <Destinasyon> Tatil Paketi"; destinasyon boşsa o sözcük atlanır. */
export function belgeBasligi(musteriAd: string, destinasyon: string): string {
  const d = destinasyon.trim();
  return `${musteriAd.trim()} - Özel ${d === '' ? '' : `${d} `}Tatil Paketi`;
}

/** Ekranda her zaman sunulan gezi ve transfer paketleri, ekran sırasıyla. */
export const PAKET_SIRASI: readonly PaketKodu[] = ['TRANSFER', 'DUBAI_TURU', 'ABU_DABI_TURU', 'SAFARI_TURU', 'HELIKOPTER_TURU', 'GOKYUZUNDE_YEMEK', 'OZEL_YAT_TURU'];

/** Kişi başı fiyatlı geziler: araç tipi, tarih ve saat sorulmaz; tarih ve saati satış sonrası operasyon planlar. */
export const KISI_BASI_PAKETLER: readonly PaketKodu[] = ['SAFARI_TURU', 'HELIKOPTER_TURU', 'GOKYUZUNDE_YEMEK'];

/** Saat başı yat başı fiyatlı paket (özel yat): saat sayısı ve yat boyutu girilir, araç tipi ve tarih sorulmaz. */
export const SAAT_BASI_PAKETLER: readonly PaketKodu[] = ['OZEL_YAT_TURU'];
export const YAT_SAAT = { varsayilan: 2, enAz: 1, enCok: 12 } as const;
export const YAT_BOYUTLARI: readonly YatBoyutu[] = ['YAT_50FT', 'YAT_70FT'];

/** Yat saat girişi (dize) -> geçerli tam sayı ya da null. Boş giriş varsayılan 2 sayılmaz: ekran alanı dolu başlar. */
export function yatSaatiCoz(metin: string): number | null {
  if (!/^\d{1,2}$/.test(metin.trim())) return null;
  const n = Number(metin);
  return n >= YAT_SAAT.enAz && n <= YAT_SAAT.enCok ? n : null;
}

/** Sunucu isteğine giden yat seçimi: saat geçersizse varsayılan (2) gönderilmez, istek kapıda durur (kapı 'yat'). */
export const yatIstegi = (saat: string, boyut: YatBoyutu | null): YatSecimi | null => {
  const n = yatSaatiCoz(saat);
  return n === null ? null : { saat: n, boyut };
};

/**
 * Gezi ve transfer paketi anahtarları HER ZAMAN seçilebilir (Musa kararı 28.10.2026): Dubai'den araçla Abu Dabi'ye gidilir,
 * otelin Abu Dabi'de olması gerekmez. Eski kural paketi otel şehrine bağlıyordu (Abu Dabi turu yalnız Abu Dabi oteliyle
 * görünürdü); kural kaldırıldı, paket görünürlüğü otellerden bağımsızdır.
 */
export function paketKullanilabilir(): readonly PaketKodu[] {
  return PAKET_SIRASI;
}

export function otelEkle(liste: readonly OtelGirdisi[], yeni: OtelGirdisi): { readonly liste: readonly OtelGirdisi[]; readonly sonuc: 'eklendi' | 'var' | 'dolu' } {
  if (liste.some((o) => o.girdi === yeni.girdi || o.slug === yeni.slug)) return { liste, sonuc: 'var' };
  if (liste.length >= POLITIKA.azamiOtel) return { liste, sonuc: 'dolu' };
  return { liste: [...liste, yeni], sonuc: 'eklendi' };
}

/** Sunucu sonucunu otel girdisiyle eşler: önce `girdi`, sonra otel no ve slug. */
export function sonucBul(sonuclar: readonly OtelSonucu[], o: OtelGirdisi): OtelSonucu | null {
  return sonuclar.find((s) => s.girdi === o.girdi) ?? sonuclar.find((s) => (s.otelNo !== null && s.otelNo === o.otelNo) || (s.slug !== null && s.slug === o.slug)) ?? null;
}

// ---- Seçim ve toplam -----------------------------------------------------------------------------------------

export type Secim = Readonly<Record<string, string>>;

/**
 * Varsayılan seçenek (şartname 6): uygun tipler içinde en düşük toplam; "çocuk için ek yatak" varsayılan olmaz,
 * yalnız başka uygun seçenek yoksa. Sunucunun `varsayilanSecenekNo`su ek yatağı da saydığı için ekran kendi kuralını uygular.
 */
export function varsayilanSecenekNo(g: OdaGrubu): string | null {
  const sirali = [...g.secenekler].sort((a, b) => a.toplamMinor - b.toplamMinor);
  const uygun = sirali.find((s) => s.kapasite === 'UYGUN' || s.kapasite === 'BILINMIYOR');
  return (uygun ?? sirali.find((s) => s.kapasite === 'EK_YATAKLI'))?.secenekNo ?? null;
}

/** Satışçının seçimi (grup anahtarı -> seçenek no); verilmeyen grup varsayılanı alır. */
export function secilenSecenek(g: OdaGrubu, secim: Secim): OdaSecenegi | null {
  const no = secim[g.anahtar] ?? varsayilanSecenekNo(g);
  if (no === null) return null;
  const s = g.secenekler.find((x) => x.secenekNo === no) ?? null;
  return s !== null && s.kapasite === 'UYMUYOR' ? null : s;
}

export interface SatirHesabi {
  readonly secilenler: readonly { readonly grup: OdaGrubu; readonly secenek: OdaSecenegi }[];
  /** Seçeneği olmayan (uymayan) grupların oda numaraları. */
  readonly uymayanOdalar: readonly number[];
  readonly toplamMinor: number | null;
  readonly paraBirimi: ParaBirimi | null;
  readonly paraKarisik: boolean;
}

export function satirHesapla(sonuc: OtelSonucu, secim: Secim): SatirHesabi {
  const secilenler: { grup: OdaGrubu; secenek: OdaSecenegi }[] = [];
  const uymayan: number[] = [];
  for (const g of sonuc.odaGruplari) {
    const s = secilenSecenek(g, secim);
    if (s === null) uymayan.push(...g.odaNolari);
    else secilenler.push({ grup: g, secenek: s });
  }
  const paralar = new Set(secilenler.map((x) => x.secenek.paraBirimi));
  const tam = sonuc.odaGruplari.length > 0 && uymayan.length === 0 && paralar.size === 1;
  return {
    secilenler,
    uymayanOdalar: [...uymayan].sort((a, b) => a - b),
    toplamMinor: tam ? secilenler.reduce((t, x) => t + x.secenek.toplamMinor, 0) : null,
    paraBirimi: paralar.size === 1 ? ([...paralar][0] ?? null) : null,
    paraKarisik: paralar.size > 1,
  };
}

/**
 * "Booking fiyatını çek" düğmesi açık mı (03.11.2026). Tek odada da çok odada da açık; kapalı olduğu yalnız üç durum:
 * teklif salt okunur, otelin katalog adresi yok, hiç oda yok.
 */
export function bookingCekilebilir(a: { readonly saltOkunur: boolean; readonly slug: string | null; readonly odaSayisi: number }): boolean {
  return !a.saltOkunur && a.slug !== null && a.odaSayisi >= 1;
}

/**
 * Oda numarasına (1'den) göre seçilen oda tipinin adı. Aynı düzendeki odalar tek grupta toplanır
 * (`OdaGrubu.odaNolari`), her biri aynı seçenekle eşleşir. Seçimi olmayan oda `null`.
 */
export function secilenOdaAdlari(secilenler: SatirHesabi['secilenler'], odaSayisi: number): (string | null)[] {
  const adlar: (string | null)[] = Array.from({ length: odaSayisi }, () => null);
  for (const { grup, secenek } of secilenler) {
    for (const no of grup.odaNolari) {
      if (no >= 1 && no <= odaSayisi) adlar[no - 1] = secenek.odaTipi;
    }
  }
  return adlar;
}

/**
 * Çok odalı Booking isteğinin oda listesi: her oda kendi doluluğu (yetişkin, çocuk yaşları) ve seçilen oda tipinin
 * adıyla gider. Ad yoksa alan hiç yazılmaz; sunucu o dolulukta en ucuz vergi dahil odayı alır.
 */
export function bookingOdaIstegi(odalar: readonly OdaDuzeni[], adlar: readonly (string | null)[]): { yetiskin: number; cocukYaslari?: number[]; odaAdi?: string }[] {
  return odalar.map((o, i) => {
    const ad = adlar[i];
    return {
      yetiskin: o.yetiskin,
      ...(o.cocukYaslari.length === 0 ? {} : { cocukYaslari: [...o.cocukYaslari] }),
      ...(ad === null || ad === undefined || ad.trim() === '' ? {} : { odaAdi: ad.trim() }),
    };
  });
}

/** Varsayılana göre fark (minor). Varsayılan seçenek yoksa null. */
export function varsayilanaFark(g: OdaGrubu, s: OdaSecenegi): number | null {
  const v = g.secenekler.find((x) => x.secenekNo === varsayilanSecenekNo(g));
  return v === undefined ? null : s.toplamMinor - v.toplamMinor;
}

/** "Aile odası + 2 Standart çift": aynı düzendeki birden çok oda adetle yazılır. */
export function kombinasyonAdi(secilenler: readonly { readonly grup: OdaGrubu; readonly secenek: OdaSecenegi }[]): string {
  return secilenler.map((x) => (x.grup.adet > 1 ? `${x.grup.adet} ${x.secenek.odaTipi}` : x.secenek.odaTipi)).join(' + ');
}

/** En erken geçerlilik bitişi (ISO). */
export function enErkenBitis(secenekler: readonly OdaSecenegi[]): string | null {
  let en: string | null = null;
  for (const s of secenekler) {
    const t = Date.parse(s.quoteExpiresAt);
    if (!Number.isFinite(t)) continue;
    if (en === null || t < Date.parse(en)) en = s.quoteExpiresAt;
  }
  return en;
}

/** En sıkı iptal koşulu: bir satırdaki birden çok odadan en kötüsü satırı temsil eder. */
export function iptalOzeti(secenekler: readonly OdaSecenegi[]): { readonly tur: 'ucretsiz' | 'iadesiz' | 'bilinmiyor'; readonly sonTarih: string | null } {
  if (secenekler.length === 0) return { tur: 'bilinmiyor', sonTarih: null };
  const k: IptalKosulu[] = secenekler.map((s) => s.iptal);
  if (k.some((x) => x.iadeEdilebilir === false)) return { tur: 'iadesiz', sonTarih: null };
  if (k.some((x) => x.iadeEdilebilir === null || x.sonTarih === null)) return { tur: 'bilinmiyor', sonTarih: null };
  const en = k.map((x) => x.sonTarih as string).sort((a, b) => Date.parse(a) - Date.parse(b))[0] ?? null;
  return { tur: 'ucretsiz', sonTarih: en };
}

// ---- Satır durumu --------------------------------------------------------------------------------------------

export const DURUM_BILGISI: Readonly<Record<DurumKodu, { readonly sekil: DurumSekli; readonly ton: DurumTonu }>> = {
  bekliyor: { sekil: 'bos-daire', ton: 'notr' },
  cekiliyor: { sekil: 'halka', ton: 'vurgu' },
  hazir: { sekil: 'dolu-daire', ton: 'ok' },
  talep: { sekil: 'kare', ton: 'uyari' },
  yok: { sekil: 'eskenar', ton: 'tehlike' },
  yanit: { sekil: 'eskenar', ton: 'tehlike' },
  sure: { sekil: 'ucgen', ton: 'tehlike' },
  eski: { sekil: 'etiket', ton: 'notr' },
  uymuyor: { sekil: 'carpi', ton: 'tehlike' },
};

/** Sunucu hata sınıfı -> satır durumu. */
export function hataDurumu(sinif: NonNullable<OtelSonucu['hata']>['sinif']): DurumKodu {
  switch (sinif) {
    case 'FIYAT_ISTEGE_BAGLI':
    case 'FIYAT_SUPHELI':
      return 'talep';
    case 'MUSAIT_DEGIL':
    case 'EN_AZ_GECE':
    case 'FIYATLANAMAZ':
    case 'OTEL_BULUNAMADI':
      return 'yok';
    default:
      return 'yanit';
  }
}

export interface SatirGirdisi {
  readonly otel: OtelGirdisi;
  readonly sonuc: OtelSonucu | null;
  readonly secim: Secim;
  /** Fiyat işi sürüyor ve bu otelin sonucu henüz yok. */
  readonly bekleyenSirasi: number | null;
  readonly eskiTalep: boolean;
  readonly simdi: number;
}

export interface SatirDurumu {
  readonly kod: DurumKodu;
  /** Satırın ek durumları (rozet yanında gösterilir). */
  readonly yeni: boolean;
  readonly hesap: SatirHesabi | null;
  readonly bitis: string | null;
  readonly kalan: number | null;
  readonly sure: SureSeviyesi;
  /** Onay kutusu açılabilir mi. */
  readonly secilebilir: boolean;
  /** Çıktıya girebilir mi (seçili olsa). */
  readonly gecerli: boolean;
}

export function satirDurumu(g: SatirGirdisi, yeniGelenler: ReadonlySet<string> = new Set()): SatirDurumu {
  const bos = (kod: DurumKodu): SatirDurumu => ({ kod, yeni: false, hesap: null, bitis: null, kalan: null, sure: 'bilinmiyor', secilebilir: false, gecerli: false });
  if (g.sonuc === null) return bos(g.bekleyenSirasi !== null && g.bekleyenSirasi < POLITIKA.eszamanliCekim ? 'cekiliyor' : 'bekliyor');
  const yeni = yeniGelenler.has(g.otel.girdi);
  if (g.sonuc.durum === 'HATA') {
    const h = g.sonuc.hata;
    return { ...bos(g.eskiTalep ? 'eski' : hataDurumu(h?.sinif ?? 'BEKLENMEDIK')), yeni };
  }
  const hesap = satirHesapla(g.sonuc, g.secim);
  const bitis = enErkenBitis(hesap.secilenler.map((x) => x.secenek));
  const kalan = kalanMs(bitis, g.simdi);
  const sure = sureSeviyesi(kalan);
  if (g.eskiTalep) return { kod: 'eski', yeni, hesap, bitis, kalan, sure, secilebilir: false, gecerli: false };
  if (hesap.toplamMinor === null) return { kod: 'uymuyor', yeni, hesap, bitis, kalan, sure, secilebilir: false, gecerli: false };
  if (sure === 'doldu') return { kod: 'sure', yeni, hesap, bitis, kalan, sure, secilebilir: false, gecerli: false };
  return { kod: 'hazir', yeni, hesap, bitis, kalan, sure, secilebilir: true, gecerli: true };
}

/** "Müsait değil, 5 gece min." için en az gece sayısı (sunucu açıklamasından). */
export function enAzGece(sonuc: OtelSonucu): number | null {
  if (sonuc.hata?.sinif !== 'EN_AZ_GECE') return null;
  const e = /(\d{1,3})/.exec(sonuc.hata.aciklama);
  return e === null ? null : Number(e[1]);
}

// ---- Paketler ------------------------------------------------------------------------------------------------

export interface PaketDurumu {
  readonly acik: readonly PaketFiyati[];
  /** Açık ama fiyatı bilinmeyen (ya da para birimi uyuşmayan) paketler: "dahil" sayılmaz. */
  readonly fiyatsiz: readonly PaketFiyati[];
  readonly tamam: boolean;
}

export function paketDurumu(acikKodlar: readonly PaketKodu[], fiyatlar: readonly PaketFiyati[], para: ParaBirimi | null): PaketDurumu {
  const acik: PaketFiyati[] = [];
  const fiyatsiz: PaketFiyati[] = [];
  for (const kod of acikKodlar) {
    const f = fiyatlar.find((x) => x.kod === kod);
    if (f === undefined || f.tutarMinor === null || f.paraBirimi === null || (para !== null && f.paraBirimi !== para)) {
      fiyatsiz.push(f ?? { kod, ad: kod, kapsam: '', tutarMinor: null, paraBirimi: null });
    } else acik.push(f);
  }
  return { acik, fiyatsiz, tamam: fiyatsiz.length === 0 };
}

export interface SeceneToplami {
  readonly konaklamaMinor: number;
  readonly paketMinor: number;
  readonly toplamMinor: number | null;
  readonly para: ParaBirimi;
}

/** Seçenek toplamı: konaklama + paketler. Fiyatsız açık paket varsa toplam null (Tamamlanmadı). */
export function secenekToplami(hesap: SatirHesabi, paket: PaketDurumu): SeceneToplami | null {
  if (hesap.toplamMinor === null || hesap.paraBirimi === null) return null;
  const paketMinor = paket.acik.reduce((t, p) => t + (p.tutarMinor ?? 0), 0);
  return { konaklamaMinor: hesap.toplamMinor, paketMinor, toplamMinor: paket.tamam ? hesap.toplamMinor + paketMinor : null, para: hesap.paraBirimi };
}

// ---- Çıktı kapısı --------------------------------------------------------------------------------------------

export interface KapiGirdisi {
  readonly sonucVar: boolean;
  readonly cekimBitti: boolean;
  readonly yasTamam: boolean;
  readonly talepGuncel: boolean;
  readonly salt: boolean;
  readonly paketTamam: boolean;
  /** Bölüm tarihleri geçerli ve ardışık (örtüşme yok). */
  readonly tarihTamam: boolean;
  /** İki ve üstü bölümde bölüm seçimlerinin para birimi aynı mı (genel toplam alınabilir). */
  readonly paraTamam: boolean;
  /** Bölüm başına işaretli (seçili) sayısı. Tek bölümde en az 1, çoklu bölümde her bölümde tam 1 olmalı. */
  readonly bolumSecimleri: readonly number[];
  /** Çoklu bölüm: her bölümde yalnız bir seçenek. */
  readonly tekSecim: boolean;
  /** Bütün bölümlerdeki işaretli satırların durumları. */
  readonly isaretli: readonly SatirDurumu[];
  /** Teklife giren katalogda olmayan otel sayısı (canlı fiyatı olmadığı için `isaretli`de değildir). */
  readonly manuelSecim?: number;
  /** Teklife giren bir manuel otel eksik ya da geçersiz (ad, şehir, oda, tutar, tarih). */
  readonly manuelGecersiz?: boolean;
  /** Bir serbest kalem eksik ya da geçersiz (ad, tutar, miktar, açıklama uzunluğu, bağlı seçenek, para birimi). */
  readonly kalemGecersiz?: boolean;
  /** Özel yat açıkken saat sayısı geçersiz. */
  readonly yatGecersiz?: boolean;
}

export type KapiNedeni = 'surum' | 'cekim' | 'yas' | 'tarih' | 'eski' | 'sonuc' | 'secimYok' | 'bolumSecim' | 'sureDoldu' | 'gecersiz' | 'para' | 'paket' | 'kalem' | 'manuel' | 'yat';

export interface Kapi {
  readonly acik: boolean;
  readonly neden: KapiNedeni | null;
}

/**
 * `cikti_acik = secili.length > 0 && secili.every(gecerli) && paket_tamam && talep_guncel && yas_tamam && cekim_bitti`
 * (şartname 7), bölümler için genişletildi: tarih ardışık, her bölümde seçim var (çoklu bölümde tam bir), para aynı.
 * Kapalıysa tek neden döner; öncelik sırası kullanıcının önce düzeltmesi gereken şeydir.
 */
export function ciktiKapisi(g: KapiGirdisi): Kapi {
  const kapali = (neden: KapiNedeni): Kapi => ({ acik: false, neden });
  if (g.salt) return kapali('surum');
  if (!g.cekimBitti) return kapali('cekim');
  if (!g.yasTamam) return kapali('yas');
  if (!g.tarihTamam) return kapali('tarih');
  if (!g.talepGuncel) return kapali('eski');
  if (!g.sonucVar) return kapali('sonuc');
  if ((g.isaretli.length === 0 && (g.manuelSecim ?? 0) === 0) || g.bolumSecimleri.length === 0) return kapali('secimYok');
  if (g.bolumSecimleri.some((n) => n === 0)) return kapali(g.bolumSecimleri.length > 1 ? 'bolumSecim' : 'secimYok');
  if (g.tekSecim && g.bolumSecimleri.some((n) => n !== 1)) return kapali('bolumSecim');
  if (g.isaretli.some((s) => s.kod === 'sure')) return kapali('sureDoldu');
  if (!g.isaretli.every((s) => s.gecerli)) return kapali('gecersiz');
  if (!g.paraTamam) return kapali('para');
  if (g.manuelGecersiz === true) return kapali('manuel');
  if (g.yatGecersiz === true) return kapali('yat');
  if (!g.paketTamam) return kapali('paket');
  if (g.kalemGecersiz === true) return kapali('kalem');
  return { acik: true, neden: null };
}

/** İşaretlilerden en az biri kritik eşikte mi (çıktı oluştururken onay istenir). */
export function kritikVar(isaretli: readonly SatirDurumu[]): boolean {
  return isaretli.some((s) => s.sure === 'kritik');
}

// ---- Sıralama ------------------------------------------------------------------------------------------------

export interface SiraGirdisi {
  readonly girdi: string;
  /** Fiyatsız satırlar (talep, müsait değil, hata, uymuyor, bekleyen) null. */
  readonly toplamMinor: number | null;
}

/** Fiyata göre artan; fiyatsızlar her zaman sonda ve kendi aralarında önceki sırayı korur (kararlı). */
export function fiyataGoreSirala(satirlar: readonly SiraGirdisi[]): string[] {
  return satirlar
    .map((s, i) => ({ s, i }))
    .sort((a, b) => {
      const x = a.s.toplamMinor;
      const y = b.s.toplamMinor;
      if (x === null && y === null) return a.i - b.i;
      if (x === null) return 1;
      if (y === null) return -1;
      return x - y || a.i - b.i;
    })
    .map((e) => e.s.girdi);
}

/** Sıra anahtarı listesini otel listesine uygular; listede olmayanlar sona (ekleme sırasıyla). */
export function siraUygula<T extends { readonly girdi: string }>(oteller: readonly T[], sira: readonly string[] | null): T[] {
  if (sira === null) return [...oteller];
  const yer = new Map(sira.map((k, i) => [k, i] as const));
  return oteller.map((o, i) => ({ o, i })).sort((a, b) => (yer.get(a.o.girdi) ?? 1e9 + a.i) - (yer.get(b.o.girdi) ?? 1e9 + b.i)).map((e) => e.o);
}

export type SiraKarari = 'bekle' | 'sirala' | 'dokunma' | 'sor';

/**
 * Çekim bitince bir kez: kullanıcı tabloyla son 3 sn içinde etkileştiyse satırlar yerinde kalır ve düğme görünür.
 */
export function siraKarari(g: { readonly cekimBitti: boolean; readonly zatenSiralandi: boolean; readonly sonEtkilesim: number | null; readonly simdi: number }): SiraKarari {
  if (!g.cekimBitti) return 'bekle';
  if (g.zatenSiralandi) return 'dokunma';
  if (g.sonEtkilesim !== null && g.simdi - g.sonEtkilesim < POLITIKA.etkilesimPenceresiMs) return 'sor';
  return 'sirala';
}

// ---- Sayım ve özet -------------------------------------------------------------------------------------------

export interface Sayimlar {
  readonly hazir: number;
  readonly talep: number;
  readonly yok: number;
  readonly yanit: number;
  readonly uymuyor: number;
  readonly eski: number;
  readonly sure: number;
  readonly bekleyen: number;
}

/** Bütün sayımlar TEK durum verisinden (şartname 7): ayrı sayaç tutulmaz. */
export function sayimla(durumlar: readonly DurumKodu[]): Sayimlar {
  const n = (k: DurumKodu) => durumlar.filter((d) => d === k).length;
  return { hazir: n('hazir'), talep: n('talep'), yok: n('yok'), yanit: n('yanit'), uymuyor: n('uymuyor'), eski: n('eski'), sure: n('sure'), bekleyen: n('bekliyor') + n('cekiliyor') };
}

export function sayimMetni(s: Sayimlar, m: { readonly hazir: (n: number) => string; readonly talep: (n: number) => string; readonly yok: (n: number) => string; readonly yanit: (n: number) => string; readonly uymuyor: (n: number) => string }): string {
  const p: string[] = [];
  if (s.hazir > 0) p.push(m.hazir(s.hazir));
  if (s.talep > 0) p.push(m.talep(s.talep));
  if (s.uymuyor > 0) p.push(m.uymuyor(s.uymuyor));
  if (s.yok > 0) p.push(m.yok(s.yok));
  if (s.yanit > 0) p.push(m.yanit(s.yanit));
  return p.join(', ');
}

export function ozetAraligi(toplamlar: readonly number[]): { readonly en: number; readonly ust: number } | null {
  if (toplamlar.length === 0) return null;
  return { en: Math.min(...toplamlar), ust: Math.max(...toplamlar) };
}

// ---- Teklif seçimi (PDF, mesaj ve kayıt gövdesi) -------------------------------------------------------------

export interface BolumSecimGirdisi {
  /** Bölümün fiyat işi (çekim henüz yapılmadıysa null; kapı zaten kapalıdır). */
  readonly fiyatIsiId?: string | null;
  readonly anahtar: string;
  readonly etiket: string;
  readonly giris: string;
  readonly cikis: string;
  readonly satirlar: readonly { readonly otel: OtelGirdisi; readonly sonuc: OtelSonucu; readonly durum: SatirDurumu; readonly paket: PaketDurumu; readonly isId?: string | null; readonly ekNot?: string; readonly satisToplamMinor?: number }[];
  /** Teklife giren katalogda olmayan oteller (28.10.2026): toplamı bölüm tarihlerinden hesaplanmış. */
  readonly manuelSatirlar?: readonly { readonly manuel: ManuelOtel; readonly toplamMinor: number; readonly paket: PaketDurumu }[];
}

export interface SecimGirdisi {
  readonly paketDuzenlemeleri?: readonly PaketDuzenlemesi[];
  readonly musteriId: string;
  readonly musteriAd: string;
  readonly destinasyon: string;
  readonly odalar: readonly OdaDuzeni[];
  readonly uyruk: string;
  readonly bolumler: readonly BolumSecimGirdisi[];
  /** Çoklu bölümde planın tamamı için paket durumu (tek bölümde satır paketleri kullanılır). */
  readonly planPaket: PaketDurumu;
  /** Satışçının serbest notları; yalnız PDF isteğine girer (mesaj taslağına girmez). */
  readonly teklifNotu?: string;
  readonly paketNotlari?: Readonly<Partial<Record<PaketKodu, string>>>;
  readonly aracSecimi?: AracSecimi;
  readonly serbestKalemler?: readonly SerbestKalem[];
  readonly kdvNotu?: string;
  readonly planlamaMetni?: string;
  readonly isleyisMetni?: string;
  readonly safariBebekKoltukSayisi?: number;
  readonly yat?: YatSecimi;
}

/**
 * Ekrandaki işaretli seçimin birebir kopyası. Fiyatı olmayan paket, para karışıklığı ya da bölümsüz seçim varsa null
 * (kapı zaten kapalıdır). Tek bölümde her seçeneğin toplamı konaklama + paket; çoklu bölümde her bölümde TEK seçenek
 * ve `genelToplamMinor` = bölüm seçimleri + planın paketleri.
 */
export function teklifSecimiKur(g: SecimGirdisi): TeklifSecimi | null {
  const coklu = g.bolumler.length > 1;
  const gruplar: SecimGrubu[] = [];
  for (const b of g.bolumler) {
    const secenekler: SecimOtel[] = [];
    for (const s of b.satirlar) {
      const hesap = s.durum.hesap;
      if (hesap === null || hesap.toplamMinor === null || hesap.paraBirimi === null || s.durum.bitis === null) return null;
      const top = secenekToplami(hesap, coklu ? { acik: [], fiyatsiz: [], tamam: true } : s.paket);
      if (top === null || top.toplamMinor === null) return null;
      const odalar: SecimOdaSatiri[] = hesap.secilenler.map((x) => ({
        grupAnahtari: x.grup.anahtar,
        odaNolari: x.grup.odaNolari,
        adet: x.grup.adet,
        duzen: x.grup.duzen,
        secenekNo: x.secenek.secenekNo,
        aramaNo: x.secenek.aramaNo,
        odaTipi: x.secenek.odaTipi,
        kahvalti: x.secenek.kahvalti,
        iptal: x.secenek.iptal,
        toplamMinor: x.secenek.toplamMinor,
      }));
      secenekler.push({
        ...(s.ekNot === undefined || s.ekNot.trim() === '' ? {} : { ekNot: s.ekNot.trim() }),
        ...(s.satisToplamMinor === undefined ? {} : { satisToplamMinor: s.satisToplamMinor }),
        fiyatIsiId: s.isId ?? b.fiyatIsiId ?? null,
        otelNo: s.sonuc.otelNo ?? s.otel.otelNo,
        slug: s.sonuc.slug ?? s.otel.slug,
        otelAdi: s.sonuc.otelAdi ?? s.otel.ad,
        odalar,
        konaklamaMinor: hesap.toplamMinor,
        paketler: coklu ? [] : s.paket.acik.map((p) => ({ kod: p.kod, ad: p.ad, kapsam: p.kapsam, tutarMinor: p.tutarMinor ?? 0 })),
        secenekMinor: top.toplamMinor,
        paraBirimi: top.para,
        gecerlilikBitis: s.durum.bitis,
      });
    }
    for (const x of b.manuelSatirlar ?? []) {
      const top = secenekToplami({ secilenler: [], uymayanOdalar: [], toplamMinor: x.toplamMinor, paraBirimi: x.manuel.paraBirimi, paraKarisik: false }, coklu ? { acik: [], fiyatsiz: [], tamam: true } : x.paket);
      if (top === null || top.toplamMinor === null) return null;
      secenekler.push({
        manuel: x.manuel,
        fiyatIsiId: null,
        otelNo: null,
        slug: manuelAnahtar(x.manuel.id),
        otelAdi: x.manuel.ad.trim(),
        odalar: [],
        konaklamaMinor: x.toplamMinor,
        paketler: coklu ? [] : x.paket.acik.map((p) => ({ kod: p.kod, ad: p.ad, kapsam: p.kapsam, tutarMinor: p.tutarMinor ?? 0 })),
        secenekMinor: top.toplamMinor,
        paraBirimi: top.para,
        gecerlilikBitis: '',
      });
    }
    if (secenekler.length === 0) return null;
    const gece = gunSayisi(b.giris, b.cikis);
    if (gece === null) return null;
    gruplar.push({ fiyatIsiId: b.fiyatIsiId ?? null, anahtar: b.anahtar, etiket: b.etiket, girisTarihi: b.giris, cikisTarihi: b.cikis, geceSayisi: gece, secenekler });
  }
  if (gruplar.length === 0) return null;
  const hepsi = gruplar.flatMap((x) => x.secenekler);
  let genel: number | null = null;
  let genelPara: ParaBirimi | null = null;
  const planPaketleri = coklu ? g.planPaket.acik.map((p) => ({ kod: p.kod, ad: p.ad, kapsam: p.kapsam, tutarMinor: p.tutarMinor ?? 0 })) : [];
  if (coklu) {
    if (!g.planPaket.tamam) return null;
    const paralar = new Set(hepsi.map((o) => o.paraBirimi));
    if (paralar.size !== 1) return null;
    genelPara = [...paralar][0] ?? null;
    genel = gruplar.reduce((t, x) => t + (x.secenekler[0]?.secenekMinor ?? 0), 0) + planPaketleri.reduce((t, p) => t + p.tutarMinor, 0);
  }
  const acikKodlar = new Set((coklu ? g.planPaket.acik : (gruplar[0]?.secenekler[0]?.paketler ?? [])).map((p) => p.kod));
  const geziNotlari = Object.fromEntries(Object.entries(g.paketNotlari ?? {}).filter(([k, v]) => acikKodlar.has(k as PaketKodu) && typeof v === 'string' && v.trim() !== '').map(([k, v]) => [k, (v as string).trim()])) as Partial<Record<PaketKodu, string>>;
  const bitis = hepsi.map((o) => o.gecerlilikBitis).filter((x) => x !== '').sort((a, b) => Date.parse(a) - Date.parse(b))[0] ?? '';
  const kalemler = (g.serbestKalemler ?? []).filter((k) => k.ad.trim() !== '' || k.tutar.trim() !== '');
  const kdv = (g.kdvNotu ?? '').trim();
  const planlama = (g.planlamaMetni ?? '').trim();
  const isleyis = (g.isleyisMetni ?? '').trim();
  return { ...(g.paketDuzenlemeleri === undefined ? {} : { paketDuzenlemeleri: g.paketDuzenlemeleri }), ...(g.aracSecimi === undefined ? {} : { aracSecimi: g.aracSecimi }), musteriId: g.musteriId, musteriAdi: g.musteriAd, odalar: g.odalar, uyruk: g.uyruk, gruplar, planPaketleri, ...(g.teklifNotu === undefined || g.teklifNotu.trim() === '' ? {} : { teklifNotu: g.teklifNotu.trim() }), ...(Object.keys(geziNotlari).length === 0 ? {} : { geziEkNotlari: geziNotlari }), ...(kalemler.length === 0 ? {} : { serbestKalemler: kalemler }), ...(kdv === '' ? {} : { kdvNotu: kdv }), ...(planlama === '' ? {} : { planlamaMetni: planlama }), ...(isleyis === '' ? {} : { isleyisMetni: isleyis }), ...(g.safariBebekKoltukSayisi === undefined ? {} : { safariBebekKoltukSayisi: g.safariBebekKoltukSayisi }), ...(g.yat === undefined ? {} : { yat: g.yat }), genelToplamMinor: genel, genelParaBirimi: genelPara, destinasyon: g.destinasyon, belgeBasligi: belgeBasligi(g.musteriAd, g.destinasyon), gecerlilikBitis: bitis };
}

// ---- Konaklama bölümleri: tarih doğrulaması (Musa kararı 26.10.2026) --------------------------------------------

export const AZAMI_BOLUM = 3;

export interface BolumTarihi {
  readonly giris: string;
  readonly cikis: string;
}

export type BolumTarihHatasi = 'ortusme' | 'eksik' | null;

/**
 * Bölüm tarihleri ardışık olmalı: her bölümün girişi bir öncekinin çıkışından önce olamaz (örtüşme). Çıkış = sonraki
 * giriş serbesttir (aynı gün otel değişimi), arada boşluk da serbesttir. Tek bölümde yalnız "eksik" olabilir.
 * Hata, hatalı bölümün kendi indeksine yazılır (sonraki bölüm).
 */
export function bolumTarihHatalari(b: readonly BolumTarihi[]): readonly BolumTarihHatasi[] {
  return b.map((x, i) => {
    if (x.giris === '' || x.cikis === '' || gunSayisi(x.giris, x.cikis) === null) return 'eksik';
    const onceki = b[i - 1];
    if (onceki !== undefined && onceki.cikis !== '' && x.giris < onceki.cikis) return 'ortusme';
    return null;
  });
}

/** "14 Kas 2026 - 18 Kas 2026" yerine kısa: "14 Kas - 18 Kas". */
export const tarihAraligiKisa = (giris: string, cikis: string): string => `${gunAyKisa(giris)} - ${gunAyKisa(cikis)}`;

// ---- Duyuru kuyruğu (en çok 5 sn'de bir) ---------------------------------------------------------------------

/**
 * Tek canlı bölgeye yazılacak metinler. İki duyuru arası en az `aralikMs`; beklerken biriken metinler kaybolmaz,
 * pencere dolunca tek cümlede birleşir. Aynı metin arka arkaya iki kez yazılmaz. Saat dışarıdan verilir (test).
 */
export class DuyuruKuyrugu {
  private bekleyen: string[] = [];
  private son = Number.NEGATIVE_INFINITY;
  private sonMetin = '';
  constructor(private readonly aralikMs: number = POLITIKA.duyuruAraligiMs) {}

  ekle(metin: string): void {
    if (metin.trim() === '') return;
    if (this.bekleyen[this.bekleyen.length - 1] === metin) return;
    this.bekleyen.push(metin);
  }

  /** Pencere dolduysa ve bekleyen varsa birleşik metni döndürür ve pencereyi yeniden başlatır. */
  isle(simdi: number): string | null {
    if (this.bekleyen.length === 0 || simdi - this.son < this.aralikMs) return null;
    const metin = [...new Set(this.bekleyen)].join(' ');
    this.bekleyen = [];
    if (metin === this.sonMetin) return null;
    this.son = simdi;
    this.sonMetin = metin;
    return metin;
  }

  get bekleyenSayisi(): number {
    return this.bekleyen.length;
  }

  temizle(): void {
    this.bekleyen = [];
  }
}

/** Süre eşiği duyurusu bir otel için eşik başına yalnız bir kez verilir. */
export class EsikTakibi {
  private verilen = new Set<string>();
  /** Yeni eşik geçildiyse eşik adını döndürür. */
  kontrol(girdi: string, seviye: SureSeviyesi): 'bilgi' | 'kritik' | 'doldu' | null {
    if (seviye !== 'bilgi' && seviye !== 'kritik' && seviye !== 'doldu') return null;
    const k = `${girdi}|${seviye}`;
    if (this.verilen.has(k)) return null;
    this.verilen.add(k);
    return seviye;
  }
  sifirla(girdi?: string): void {
    if (girdi === undefined) this.verilen.clear();
    else for (const k of [...this.verilen]) if (k.startsWith(`${girdi}|`)) this.verilen.delete(k);
  }
}

// ---- Tutar girişi: binlik ve ondalık, kuruş hatasız (minor birim) ----------------------------------------------------

/**
 * Yazılan tutar -> minor birim (kuruş), YALNIZ tam sayı aritmetiğiyle (ondalık kayması yok). Kabul: "1234", "1.234",
 * "1.234,56", "1,234.56", "1234,5", "0,05". Ayraç kuralı: iki ayraç da varsa sonuncusu ondalıktır; tek çeşit ayraç birden
 * çok geçiyorsa binliktir; tek ayraç ve ardından tam üç hane varsa binliktir ("1.234" = 1234), aksi halde ondalıktır
 * (en çok iki hane). Eksi işareti, harf, üç haneden uzun ondalık ve boş giriş geçersizdir (null).
 */
export function tutarCoz(metin: string): number | null {
  const t = metin.trim().replace(/\s+/g, '');
  if (t === '' || !/^[0-9][0-9.,]*$/.test(t) || /[.,]{2,}/.test(t) || /[.,]$/.test(t)) return null;
  const nokta = t.split('.').length - 1;
  const virgul = t.split(',').length - 1;
  let tam: string;
  let ondalik = '';
  if (nokta > 0 && virgul > 0) {
    const sonNokta = t.lastIndexOf('.');
    const sonVirgul = t.lastIndexOf(',');
    const ondalikAyraci = sonNokta > sonVirgul ? '.' : ',';
    const binlikAyraci = ondalikAyraci === '.' ? ',' : '.';
    if ((ondalikAyraci === '.' ? nokta : virgul) !== 1) return null;
    const [t1, t2] = t.split(ondalikAyraci) as [string, string];
    tam = t1.split(binlikAyraci).join('');
    ondalik = t2;
    if (!binlikGecerli(t1, binlikAyraci)) return null;
  } else if (nokta > 0 || virgul > 0) {
    const ayrac = nokta > 0 ? '.' : ',';
    const adet = nokta > 0 ? nokta : virgul;
    const parcalar = t.split(ayrac);
    const son = parcalar[parcalar.length - 1] as string;
    if (adet > 1) {
      if (!binlikGecerli(t, ayrac)) return null;
      tam = parcalar.join('');
    } else if (son.length === 3 && parcalar[0] !== '0' && (parcalar[0] as string).length >= 1 && (parcalar[0] as string).length <= 3) {
      tam = parcalar.join('');
    } else {
      tam = parcalar[0] as string;
      ondalik = son;
    }
  } else {
    tam = t;
  }
  if (ondalik.length > 2 || !/^[0-9]+$/.test(tam) || (ondalik !== '' && !/^[0-9]+$/.test(ondalik))) return null;
  const minor = BigInt(tam) * 100n + BigInt(ondalik.padEnd(2, '0'));
  return minor <= 2_000_000_000n ? Number(minor) : null;
}

/** Girilen metin geçerli bir satış fiyatı mı? Boş metin "sistem fiyatı" demektir ve geçerlidir. */
export function satisGirdisiGecerli(metin: string): boolean {
  if (metin.trim() === '') return true;
  const n = tutarCoz(metin);
  return n !== null && n > 0;
}

/** "1.234.567" biçiminde binlik gruplar: ilk grup 1-3 hane, diğerleri tam 3 hane. */
function binlikGecerli(parca: string, ayrac: string): boolean {
  const g = parca.split(ayrac);
  return g.length > 1 && /^[0-9]{1,3}$/.test(g[0] as string) && g.slice(1).every((x) => /^[0-9]{3}$/.test(x));
}

/** Minor birim -> düzenlenebilir metin ("1234,56"); binliksiz, iki hane. */
export function tutarMetni(minor: number): string {
  return `${String(Math.trunc(minor / 100))},${String(minor % 100).padStart(2, '0')}`;
}

// ---- Serbest kalem (28.10.2026) ------------------------------------------------------------------------------------

export const KDV_VARSAYILAN = '+ %5 KDV';
export const KALEM_ACIKLAMA_AZAMI = 600;
export const KALEM_AD_AZAMI = 120;
/** "İstediği kadar kalem" (Ürün Sahibi): sınır yalnız gövde boyutunu korur; sunucudaki `KALEM_AZAMI_ADET` ile aynı (eşlik testi). */
export const KALEM_AZAMI = 100;
export const KALEM_MIKTAR_AZAMI = 999;
export const BIRIM_ETIKETI: Readonly<Record<KalemBirimi, string>> = { SABIT: 'Sabit', KISI_BASI: 'Kişi başı', GUN_BASI: 'Gün başı', SAAT_BASI: 'Saat başı' };
const MIKTAR_BIRIMI: Readonly<Record<KalemBirimi, string>> = { SABIT: '', KISI_BASI: 'kişi', GUN_BASI: 'gün', SAAT_BASI: 'saat' };

let kalemSayaci = 0;
export const yeniKalemKimligi = (): string => `kalem-${++kalemSayaci}-${Math.random().toString(36).slice(2, 8)}`;

export function yeniKalem(para: ParaBirimi = 'USD'): SerbestKalem {
  return { id: yeniKalemKimligi(), ad: '', aciklama: '', tutar: '', paraBirimi: para, birim: 'SABIT', miktar: '1', kapsam: 'TUM', secenek: null, kdvNotu: KDV_VARSAYILAN };
}

/** Birim değişince miktarın makul varsayılanı: kişi başı kişi sayısı, gün başı gece sayısı, saat başı 1, sabit 1. */
export function birimMiktari(birim: KalemBirimi, kisi: number, gece: number | null): string {
  if (birim === 'KISI_BASI') return String(Math.max(1, kisi));
  if (birim === 'GUN_BASI') return String(Math.max(1, gece ?? 1));
  return '1';
}

/** AED, USD'ye 3,6725 sabit bağlıdır (CRM'in mevcut kur kuralı: `sales-products/product-fx.ts`), yarım yukarı yuvarlama. */
const AED_MIKRO = 3_672_500n;
const KUR_OLCEK = 1_000_000n;

/** Tutarı hedef para birimine çevirir; AED-USD sabit kurla, aynı birim olduğu gibi, diğeri null (kur uydurulmaz). */
export function paraCevirMinor(minor: number, kaynak: ParaBirimi, hedef: ParaBirimi): number | null {
  if (kaynak === hedef) return minor;
  if (kaynak === 'AED' && hedef === 'USD') return Number((BigInt(minor) * KUR_OLCEK + AED_MIKRO / 2n) / AED_MIKRO);
  if (kaynak === 'USD' && hedef === 'AED') return Number((BigInt(minor) * AED_MIKRO + KUR_OLCEK / 2n) / KUR_OLCEK);
  return null;
}

export type KalemHatasi = 'AD' | 'TUTAR' | 'MIKTAR' | 'ACIKLAMA' | 'SECENEK' | 'PARA';

export interface KalemDegeri {
  readonly hata: KalemHatasi | null;
  readonly tutarMinor: number | null;
  readonly miktar: number;
  /** Kalemin kendi para biriminde toplam (tutar x miktar). */
  readonly kendiToplamMinor: number | null;
  /** Teklif para biriminde toplam; hedef para yoksa ya da çevrilemiyorsa null. */
  readonly toplamMinor: number | null;
}

/**
 * Kalemi doğrular ve toplamını hesaplar. `hedef` teklifin para birimi (seçenekler belliyse). `secenekler`: teklifte işaretli
 * otel seçeneklerinin anahtarları; `kapsam: SECENEK` kalemin seçeneği bunlardan biri olmalı.
 */
export function kalemDegerlendir(k: SerbestKalem, hedef: ParaBirimi | null, secenekler: readonly string[]): KalemDegeri {
  const tutarMinor = tutarCoz(k.tutar);
  const miktarMetni = k.birim === 'SABIT' ? '1' : k.miktar.trim();
  const miktar = /^\d{1,3}$/.test(miktarMetni) ? Number(miktarMetni) : 0;
  const bos = (hata: KalemHatasi): KalemDegeri => ({ hata, tutarMinor, miktar, kendiToplamMinor: null, toplamMinor: null });
  if (k.ad.trim() === '' || k.ad.trim().length > KALEM_AD_AZAMI) return bos('AD');
  if (tutarMinor === null) return bos('TUTAR');
  if (miktar < 1 || miktar > KALEM_MIKTAR_AZAMI) return bos('MIKTAR');
  if (k.aciklama.length > KALEM_ACIKLAMA_AZAMI) return bos('ACIKLAMA');
  if (k.kapsam === 'SECENEK' && (k.secenek === null || !secenekler.includes(k.secenek))) return bos('SECENEK');
  const kendi = tutarMinor * miktar;
  const cevrilen = hedef === null ? null : paraCevirMinor(kendi, k.paraBirimi, hedef);
  if (hedef !== null && cevrilen === null) return { hata: 'PARA', tutarMinor, miktar, kendiToplamMinor: kendi, toplamMinor: null };
  return { hata: null, tutarMinor, miktar, kendiToplamMinor: kendi, toplamMinor: cevrilen };
}

/** "300,00 USD x 7 gün" ya da "1.000,00 USD (sabit)": satırın hesap dayanağı (ekran ve özet). */
export function kalemDayanagi(k: SerbestKalem, d: KalemDegeri): string {
  if (d.tutarMinor === null) return '';
  return k.birim === 'SABIT' ? `${tutarYaz(d.tutarMinor)} ${k.paraBirimi} (sabit)` : `${tutarYaz(d.tutarMinor)} ${k.paraBirimi} x ${String(d.miktar)} ${MIKTAR_BIRIMI[k.birim]}`;
}

export interface KalemToplamlari {
  readonly tumMinor: number;
  readonly secenekMinor: ReadonlyMap<string, number>;
  /** Hatalı kalem var (kapı kapanır). */
  readonly hatali: number;
}

/** Kalemlerin toplamları: tüm seçeneklere eklenenler ve seçenek başına eklenenler. Hatalı kalem toplama girmez, sayılır. */
export function kalemToplamlari(kalemler: readonly SerbestKalem[], hedef: ParaBirimi | null, secenekler: readonly string[]): KalemToplamlari {
  let tum = 0;
  let hatali = 0;
  const secenek = new Map<string, number>();
  for (const k of kalemler) {
    const d = kalemDegerlendir(k, hedef, secenekler);
    if (d.hata !== null || d.toplamMinor === null) { hatali += 1; continue; }
    if (k.kapsam === 'TUM') tum += d.toplamMinor;
    else secenek.set(k.secenek as string, (secenek.get(k.secenek as string) ?? 0) + d.toplamMinor);
  }
  return { tumMinor: tum, secenekMinor: secenek, hatali };
}

// ---- Katalogda olmayan otel (28.10.2026) -----------------------------------------------------------------------------

export const MANUEL_ONEK = 'manuel-';
export const manuelAnahtar = (id: string): string => `${MANUEL_ONEK}${id}`;
export const manuelMi = (anahtar: string | null | undefined): boolean => anahtar !== null && anahtar !== undefined && anahtar.startsWith(MANUEL_ONEK);

let manuelSayaci = 0;
/** Sunucunun `ManuelOtelDto.id` desenine uyar: küçük harf ve rakam, 3-24 karakter. */
export const yeniManuelKimligi = (): string => `m${String(++manuelSayaci)}${Math.random().toString(36).slice(2, 8)}`.replace(/[^a-z0-9]/g, '').slice(0, 24);

export function yeniManuelOtel(): ManuelOtel {
  return { id: yeniManuelKimligi(), ad: '', sehir: '', odaAdi: '', tutarTuru: 'TOPLAM', tutar: '', paraBirimi: 'USD', gece: '', iptalMetni: '', ekNot: '' };
}

export type ManuelHatasi = 'AD' | 'SEHIR' | 'ODA' | 'TUTAR' | 'GECE' | 'TARIH' | 'IPTAL' | 'NOT';

export const MANUEL_GECE_AZAMI = 60;

/** Elle yazılan gece (dize): boş = yok (tarihlerden); 1-60 tam sayı = değer; başka her şey geçersiz (`gecerli: false`). */
export function manuelGeceCoz(metin: string): { readonly gecerli: boolean; readonly deger: number | null } {
  const t = metin.trim();
  if (t === '') return { gecerli: true, deger: null };
  if (!/^\d{1,2}$/.test(t)) return { gecerli: false, deger: null };
  const n = Number(t);
  return n >= 1 && n <= MANUEL_GECE_AZAMI ? { gecerli: true, deger: n } : { gecerli: false, deger: null };
}

export interface ManuelDegeri {
  readonly hata: ManuelHatasi | null;
  /** Tüm konaklamanın toplamı (gecelik girildiyse gecelik x gece); tarih yoksa gecelikte null. */
  readonly toplamMinor: number | null;
  readonly gecelikMinor: number | null;
  /** Hesapta kullanılan gece: elle yazılan, yoksa tarihlerden; ikisi de yoksa null. */
  readonly gece: number | null;
}

export function manuelDegerlendir(m: ManuelOtel, bolumGecesi: number | null): ManuelDegeri {
  const tutar = tutarCoz(m.tutar);
  const elle = manuelGeceCoz(m.gece);
  const gece = elle.deger ?? bolumGecesi;
  const hata = (h: ManuelHatasi): ManuelDegeri => ({ hata: h, toplamMinor: null, gecelikMinor: null, gece });
  if (m.ad.trim() === '' || m.ad.trim().length > 120) return hata('AD');
  if (m.sehir.trim() === '' || m.sehir.trim().length > 60) return hata('SEHIR');
  if (m.odaAdi.trim() === '' || m.odaAdi.trim().length > 160) return hata('ODA');
  if (tutar === null || tutar < 1) return hata('TUTAR');
  if (!elle.gecerli) return hata('GECE');
  if (m.iptalMetni.length > 300) return hata('IPTAL');
  if (m.ekNot.length > 600) return hata('NOT');
  if (m.tutarTuru === 'GECELIK') {
    if (gece === null) return { hata: 'TARIH', toplamMinor: null, gecelikMinor: tutar, gece };
    const toplam = tutar * gece;
    return toplam <= 2_000_000_000 ? { hata: null, toplamMinor: toplam, gecelikMinor: tutar, gece } : hata('TUTAR');
  }
  return { hata: null, toplamMinor: tutar, gecelikMinor: gece === null ? null : Math.floor(tutar / gece), gece };
}
